import React, { Component, type ComponentClass, type ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import { connect } from "react-redux";
import type { RouteComponentProps } from "react-router-dom";
import { Button, Modal, ModalBody, ModalHeader } from "reactstrap";
import { bindActionCreators } from "redux";
import { type InjectedFormProps, reduxForm } from "redux-form";
import MerchantGroupsView, {
  MERCHANT_GROUPS_VIEW_PATH,
} from "./MerchantGroupsView";
import { CreateMerchantGroup } from "src/merchantgroups/actions/MerchantGroupActionCreators";
import { MerchantGroupCreate } from "src/merchantgroups/formatters/MerchantGroupFormatters";
import {
  type IMerchantGroupData,
  IMerchantGroupDataToMerchantGroup,
} from "src/merchantgroups/forms/IMerchantGroupData";
import { MerchantGroupForm } from "src/merchantgroups/forms/MerchantGroupForm";
import type MerchantGroup from "src/merchantgroups/objects/MerchantGroup";
import { getMerchantGroupDetailViewPath } from "src/merchantgroups/views/MerchantGroupPaths";
import Loader from "src/shared/components/Loader";

export const CREATE_MERCHANT_GROUP_VIEW_PATH: string = "/mg/create";

export interface ICreateMerchantGroupData extends IMerchantGroupData {}

interface ICreateMerchantGroupViewInnerProps
  extends ICreateMerchantGroupViewWithReduxProps,
    InjectedFormProps<
      ICreateMerchantGroupData,
      ICreateMerchantGroupViewWithReduxProps
    > {}

interface ICreateMerchantGroupViewInnerState {
  createMerchantGroupPromise?: Promise<string>;
}

class CreateMerchantGroupViewInner extends Component<
  ICreateMerchantGroupViewInnerProps,
  ICreateMerchantGroupViewInnerState
> {
  public state = {
    createMerchantGroupPromise: undefined,
  };

  constructor(props: ICreateMerchantGroupViewInnerProps) {
    super(props);

    this.onSubmit = this.onSubmit.bind(this);
    this.onPopupAbort = this.onPopupAbort.bind(this);
  }

  public async onSubmit(formData: ICreateMerchantGroupData): Promise<void> {
    const merchantGroup: MerchantGroup =
      IMerchantGroupDataToMerchantGroup(formData);

    const { CreateMerchantGroup } = this.props;
    const createMerchantGroupPromise: Promise<string> =
      CreateMerchantGroup(merchantGroup);
    this.setState({
      createMerchantGroupPromise,
    });

    merchantGroup.merchantGroupUUID = await createMerchantGroupPromise;

    // Reload
    const { onLoad } = this.props;
    onLoad();

    // Redirect to MerchantGroups
    const { history } = this.props;
    const merchantGroupViewPath: string =
      getMerchantGroupDetailViewPath(merchantGroup);
    history.push(merchantGroupViewPath);
  }

  public onPopupAbort(): void {
    this.props.history.push(MERCHANT_GROUPS_VIEW_PATH);
  }

  public render(): ReactNode {
    const { form, handleSubmit, pristine, submitting, invalid } = this.props;
    const { createMerchantGroupPromise } = this.state;
    return (
      <Modal isOpen toggle={this.onPopupAbort}>
        <ModalHeader toggle={this.onPopupAbort}>
          <MerchantGroupCreate />
        </ModalHeader>
        <ModalBody>
          <form onSubmit={handleSubmit(this.onSubmit)}>
            <MerchantGroupForm
              form={form}
              nextButtonDisabled={submitting || invalid}
            >
              <Button
                type="submit"
                color="primary"
                disabled={pristine || submitting || invalid}
              >
                <FormattedMessage id="merchant_groups.create.submit" />
              </Button>
            </MerchantGroupForm>
            {createMerchantGroupPromise && (
              <Loader promise={createMerchantGroupPromise} />
            )}
          </form>
        </ModalBody>
      </Modal>
    );
  }
}

// Redux Forms
interface ICreateMerchantGroupViewWithReduxProps
  extends ICreateMerchantGroupViewWithActionCreatorsProps {
  CreateMerchantGroup: (merchantGroup: MerchantGroup) => Promise<string>;
}
const CreateMerchantGroupViewWithRedux: ComponentClass<ICreateMerchantGroupViewWithReduxProps> =
  reduxForm<ICreateMerchantGroupData, ICreateMerchantGroupViewWithReduxProps>({
    form: "CreateMerchantGroupView",
  })(CreateMerchantGroupViewInner);

// Redux Connect
interface ICreateMerchantGroupViewWithActionCreatorsProps
  extends IMerchantGroupCreateViewProps {
  onLoad: () => void;
}
const CreateMerchantGroupViewWithActionCreators = connect(
  () => {
    return {};
  },
  (dispatch) =>
    bindActionCreators(
      {
        CreateMerchantGroup,
      },
      dispatch,
    ),
)(CreateMerchantGroupViewWithRedux);

// Export Default
export interface IMerchantGroupCreateViewProps
  extends RouteComponentProps<void> {}
export default (props: IMerchantGroupCreateViewProps) => {
  return (
    <MerchantGroupsView {...props}>
      {(merchantGroups, onLoad) =>
        React.createElement(CreateMerchantGroupViewWithActionCreators, {
          ...props,
          onLoad,
        })
      }
    </MerchantGroupsView>
  );
};
